import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowUp, ArrowDown, MessageCircle, Trash2, Pin } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchPost();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts/${postId}`, {
        withCredentials: true
      });
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts/${postId}/comments`, {
        withCredentials: true
      });
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleVote = async (action) => {
    try {
      await axios.post(
        `${API}/forum/posts/${postId}/vote`,
        { action },
        { withCredentials: true }
      );
      fetchPost();
      toast.success('Vote recorded');
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/forum/posts/${postId}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setNewComment('');
      fetchComments();
      fetchPost(); // Refresh to update comment count
      toast.success('Comment added');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/forum/posts/${postId}`, {
        withCredentials: true
      });
      toast.success('Post deleted');
      navigate('/forum');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handlePinPost = async () => {
    try {
      await axios.put(`${API}/forum/posts/${postId}/pin`, {}, {
        withCredentials: true
      });
      fetchPost();
      toast.success('Post pin status updated');
    } catch (error) {
      console.error('Error pinning post:', error);
      toast.error('Failed to update pin status');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await axios.delete(`${API}/forum/comments/${commentId}`, {
        withCredentials: true
      });
      fetchComments();
      fetchPost();
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Post not found</h1>
          <Link to="/forum">
            <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37]">
              Back to Forum
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const canModerate = user && (user.role === 'admin' || user.user_id === post.author_id);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/forum" className="inline-flex items-center text-gray-400 hover:text-[#C5A059] transition-colors mb-8" data-testid="back-button">
            <ArrowLeft size={20} className="mr-2" />
            Back to Forum
          </Link>

          {/* Post */}
          <div className="bg-[#111111] border border-white/10 rounded-lg p-8 mb-8" data-testid="post-detail">
            <div className="flex gap-6">
              {/* Vote Section */}
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => handleVote('upvote')}
                  className="text-gray-400 hover:text-[#C5A059] transition-colors p-2 hover:bg-white/5 rounded"
                  data-testid="upvote-button"
                >
                  <ArrowUp size={24} />
                </button>
                <span className="text-white font-bold text-lg" data-testid="vote-count">{post.votes}</span>
                <button
                  onClick={() => handleVote('downvote')}
                  className="text-gray-400 hover:text-red-400 transition-colors p-2 hover:bg-white/5 rounded"
                  data-testid="downvote-button"
                >
                  <ArrowDown size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={post.author_picture} alt={post.author_name} />
                      <AvatarFallback className="bg-[#C5A059] text-black font-semibold">
                        {post.author_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-white">{post.author_name}</p>
                      <p className="text-gray-500 text-xs">
                        {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  {canModerate && (
                    <div className="flex gap-2">
                      {user.role === 'admin' && (
                        <Button
                          onClick={handlePinPost}
                          variant="ghost"
                          size="sm"
                          className="text-[#C5A059] hover:text-[#D4AF37] hover:bg-white/5"
                          data-testid="pin-button"
                        >
                          <Pin size={16} />
                        </Button>
                      )}
                      <Button
                        onClick={handleDeletePost}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-white/5"
                        data-testid="delete-post-button"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <span className="bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/30 text-xs px-2 py-1 rounded-full">
                    {post.category}
                  </span>
                  {post.pinned && (
                    <span className="bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/30 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Pin size={12} />
                      Pinned
                    </span>
                  )}
                </div>

                <h1 className="font-manrope font-bold text-3xl text-white mb-4" data-testid="post-title">
                  {post.title}
                </h1>
                <p className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap" data-testid="post-content">
                  {post.content}
                </p>

                <div className="mt-6">
                  <ShareButtons title={post.title} />
                </div>

                <div className="flex items-center gap-4 mt-6 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <MessageCircle size={16} />
                    <span>{post.comment_count} comments</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comment Form */}
          <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-8">
            <h3 className="font-manrope font-semibold text-white text-lg mb-4">Add a comment</h3>
            <form onSubmit={handleCommentSubmit}>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts..."
                className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] rounded-sm mb-4 min-h-[100px]"
                data-testid="comment-input"
              />
              <Button
                type="submit"
                disabled={submitting || !newComment.trim()}
                className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-6 py-2 font-semibold"
                data-testid="comment-submit-button"
              >
                {submitting ? 'Posting...' : 'Post Comment'}
              </Button>
            </form>
          </div>

          {/* Comments */}
          <div className="space-y-4">
            <h3 className="font-manrope font-semibold text-white text-lg mb-4">
              Comments ({comments.length})
            </h3>
            {comments.length === 0 ? (
              <p className="text-gray-400 text-center py-8" data-testid="no-comments-message">No comments yet. Be the first to comment!</p>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.comment_id}
                  className="bg-[#111111] border border-white/10 rounded-lg p-6"
                  data-testid={`comment-${comment.comment_id}`}
                >
                  <div className="flex gap-4">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarImage src={comment.author_picture} alt={comment.author_name} />
                      <AvatarFallback className="bg-[#C5A059] text-black font-semibold text-sm">
                        {comment.author_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium text-white text-sm">{comment.author_name}</span>
                          <span className="text-gray-500 text-xs ml-2">
                            {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        {user && (user.role === 'admin' || user.user_id === comment.author_id) && (
                          <Button
                            onClick={() => handleDeleteComment(comment.comment_id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-white/5"
                            data-testid={`delete-comment-${comment.comment_id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">
                        {comment.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PostDetail;
